import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { useTranslation, Trans } from 'react-i18next';
import { Switch } from 'src/@/components/ui/switch';
import Iconify from 'src/components/Iconify';
import useAuth from 'src/hooks/useAuth';
import { verifyRules } from 'src/utils/functions';
import v2_actionEditarCliente from 'src/actions/v2/cliente/v2_actionEditarCliente';
import v1_actionListaSetores from 'src/actions/v1/clientes/v1_actionListaSetores';
import {
    getDivisoes,
    criarDivisao,
    editarDivisao,
    excluirDivisao
} from 'src/redux/slices/chat-arquivo';
import ModalDivisao from 'src/sections/@chats/arquivadas/ModalDivisao';
import ModalExcluirDivisao from 'src/sections/@chats/arquivadas/ModalExcluirDivisao';
import { getCorDivisao } from 'src/sections/@chats/arquivadas/ChatsArquivadas.design';
import { ARQUIVO_CONFIG_DESIGN as D } from './ArquivoConfig.design';

export default function ArquivoConfigComponent() {
    const { t } = useTranslation('settings');
    const { cliente, user } = useAuth();
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useDispatch();

    // Teto de visibilidade da organização. A rule do usuário nunca ultrapassa isto —
    // a permissão efetiva é min(rule, teto), resolvida no backend.
    const OPCOES_VISIBILIDADE = [
        {
            value: 'proprio',
            titulo: t('archiveSettings.visibility.optionOwnTitle', 'Somente as próprias conversas'),
            descricao: t(
                'archiveSettings.visibility.optionOwnDescription',
                'Cada atendente vê apenas as conversas que ele arquivou ou que estão sob sua responsabilidade.'
            )
        },
        {
            value: 'setor',
            titulo: t('archiveSettings.visibility.optionSectorTitle', 'Conversas do setor'),
            descricao: t(
                'archiveSettings.visibility.optionSectorDescription',
                'Supervisores com permissão veem as conversas arquivadas do setor deles. Recomendado.'
            )
        },
        {
            value: 'todos',
            titulo: t('archiveSettings.visibility.optionAllTitle', 'Todas as conversas da organização'),
            descricao: t(
                'archiveSettings.visibility.optionAllDescription',
                'Usuários com permissão veem o arquivo inteiro, independente de setor.'
            )
        }
    ];

    const divisoes = useSelector((state) => state.chatArquivo.divisoes);

    // Gate pela rule do token, não pelo `permissoes` do slice: o slice só é
    // preenchido pela resposta de getDivisoes, então usá-lo aqui esconderia o
    // botão sempre que a listagem falhasse — inclusive no primeiro acesso.
    const podeGerenciar =
        user?.level === 'root' || verifyRules(user?.rules, 'arquivo.gerenciar_divisoes');

    const [visibilidade, setVisibilidade] = useState('setor');
    const [desarquivarAuto, setDesarquivarAuto] = useState(true);
    const [atualizarLastUpdate, setAtualizarLastUpdate] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [setores, setSetores] = useState([]);
    const [modalDivisao, setModalDivisao] = useState({ aberto: false, divisao: null });
    const [modalExcluir, setModalExcluir] = useState({ aberto: false, divisao: null });

    useEffect(() => {
        const arquivo = cliente?.config?.arquivo;
        if (!arquivo) return;
        if (arquivo.visibilidade_maxima) setVisibilidade(arquivo.visibilidade_maxima);
        setDesarquivarAuto(arquivo.desarquivar_auto !== false);
        setAtualizarLastUpdate(arquivo.desarquivar_atualiza_lastupdate !== false);
    }, [cliente?.config?.arquivo]);

    useEffect(() => {
        dispatch(getDivisoes());
    }, [dispatch]);

    useEffect(() => {
        if (!cliente?._id) return;
        v1_actionListaSetores(cliente._id)
            .then(({ data }) => setSetores(data?.data || []))
            .catch(() => setSetores([]));
    }, [cliente?._id]);

    const notificar = useCallback(
        (mensagem, variant = 'success') =>
            enqueueSnackbar(mensagem, {
                variant,
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            }),
        [enqueueSnackbar]
    );

    const handleSalvarDivisao = async (dados) => {
        if (modalDivisao.divisao) {
            await dispatch(editarDivisao(`${modalDivisao.divisao._id}`, dados));
            notificar(t('archiveSettings.toast.divisionUpdated', 'Divisão atualizada'));
        } else {
            await dispatch(criarDivisao(dados));
            notificar(t('archiveSettings.toast.divisionCreated', 'Divisão criada'));
        }
    };

    const handleExcluirDivisao = async (divisaoId, moverPara) => {
        await dispatch(excluirDivisao(divisaoId, moverPara));
        notificar(t('archiveSettings.toast.divisionDeleted', 'Divisão excluída'));
    };

    const nomeSetor = (setorId) =>
        setores.find((s) => `${s._id}` === `${setorId}`)?.nome_setor ||
        t('archiveSettings.divisions.sectorRemovedFallback', 'Setor removido');

    const handleSalvar = async () => {
        setSalvando(true);
        try {
            const response = await v2_actionEditarCliente(cliente._id, {
                scope: {
                    'config.arquivo.visibilidade_maxima': visibilidade,
                    'config.arquivo.desarquivar_auto': desarquivarAuto,
                    'config.arquivo.desarquivar_atualiza_lastupdate': atualizarLastUpdate
                }
            });

            if (response.data.error === false) {
                enqueueSnackbar(t('archiveSettings.toast.saveSuccess', 'Configurações de arquivamento salvas'), {
                    variant: 'success',
                    autoHideDuration: 3000,
                    anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
                });
            }
        } catch (error) {
            enqueueSnackbar(t('archiveSettings.toast.saveError', 'Não foi possível salvar as configurações'), {
                variant: 'error',
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className={D.page.root}>
            <div className={D.page.stickyHeader}>
                <div className={D.page.container}>
                    <div className={D.page.header.container}>
                        <div className={D.page.header.wrapper}>
                            <div>
                                <h1 className={D.page.header.title}>
                                    {t('archiveSettings.header.title', 'Arquivamento de conversas')}
                                </h1>
                                <p className={D.page.header.subtitle}>
                                    {t(
                                        'archiveSettings.header.subtitle',
                                        'Defina como as conversas arquivadas se comportam na sua organização.'
                                    )}
                                </p>
                            </div>
                        </div>

                        <button type="button" className={D.page.saveButton} onClick={handleSalvar} disabled={salvando}>
                            <Iconify icon="eva:save-outline" width={16} />
                            {salvando
                                ? t('archiveSettings.header.savingButton', 'Salvando...')
                                : t('buttons.saveChanges', 'Salvar alterações', { ns: 'common' })}
                        </button>
                    </div>
                </div>
            </div>

            <div className={`${D.page.container} ${D.page.pageBody}`}>
            <div className={D.section.card}>
                <div className={D.section.titleWrapper}>
                    <div className={D.section.iconBox}>
                        <Iconify icon="eva:message-circle-outline" width={18} />
                    </div>
                    <div>
                        <p className={D.section.title}>
                            {t('archiveSettings.unarchive.sectionTitle', 'Desarquivamento automático')}
                        </p>
                        <p className={D.section.subtitle}>
                            {t(
                                'archiveSettings.unarchive.sectionSubtitle',
                                'Padrão da organização — cada divisão pode sobrescrever.'
                            )}
                        </p>
                    </div>
                </div>

                <div className={D.toggle.container}>
                    <div>
                        <p className={D.toggle.label}>
                            {t(
                                'archiveSettings.unarchive.onNewMessageLabel',
                                'Desarquivar quando o contato enviar uma nova mensagem'
                            )}
                        </p>
                        <p className={D.toggle.description}>
                            {t(
                                'archiveSettings.unarchive.onNewMessageDescription',
                                'A conversa sai do arquivo e volta para Atendimentos, com alerta para o atendente. Desligado, ela permanece arquivada e apenas recebe marcação de não lida.'
                            )}
                        </p>
                    </div>
                    <Switch checked={desarquivarAuto} onCheckedChange={setDesarquivarAuto} />
                </div>

                {!desarquivarAuto && (
                    <div className={`${D.alert.container} mt-3`}>
                        <Iconify icon="eva:alert-triangle-outline" width={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className={D.alert.text}>
                            {t(
                                'archiveSettings.unarchive.disabledWarning',
                                'Com o desarquivamento desligado, mensagens de clientes em conversas arquivadas não entram na fila de atendimento e o bot não responde. Alguém precisa desarquivar manualmente para retomar o atendimento.'
                            )}
                        </p>
                    </div>
                )}

                <div className={`${D.toggle.container} mt-3 pt-3 border-t border-gray-100`}>
                    <div>
                        <p className={D.toggle.label}>
                            {t(
                                'archiveSettings.unarchive.updateDateLabel',
                                'Atualizar a data da conversa ao desarquivar'
                            )}
                        </p>
                        <p className={D.toggle.description}>
                            {t(
                                'archiveSettings.unarchive.updateDateDescription',
                                'Ligado, a conversa desarquivada sobe para o topo da lista de atendimento. Desligado, ela mantém a posição original pela data da última mensagem.'
                            )}
                        </p>
                    </div>
                    <Switch checked={atualizarLastUpdate} onCheckedChange={setAtualizarLastUpdate} />
                </div>
            </div>

            <div className={D.section.card}>
                <div className={D.section.titleWrapper}>
                    <div className={D.section.iconBox}>
                        <Iconify icon="eva:eye-outline" width={18} />
                    </div>
                    <div>
                        <p className={D.section.title}>
                            {t('archiveSettings.visibility.sectionTitle', 'Visibilidade máxima')}
                        </p>
                        <p className={D.section.subtitle}>
                            {t(
                                'archiveSettings.visibility.sectionSubtitle',
                                'Teto da organização. As permissões por usuário nunca ultrapassam este limite.'
                            )}
                        </p>
                    </div>
                </div>

                <div className="space-y-2">
                    {OPCOES_VISIBILIDADE.map((opcao) => {
                        const ativo = visibilidade === opcao.value;
                        return (
                            <div
                                key={opcao.value}
                                onClick={() => setVisibilidade(opcao.value)}
                                className={`${D.option.container} ${ativo ? D.option.active : D.option.idle}`}
                            >
                                <span
                                    className={`${D.option.radio} ${ativo ? D.option.radioActive : D.option.radioIdle}`}
                                >
                                    {ativo && <span className={D.option.radioDot} />}
                                </span>
                                <div>
                                    <p className={D.option.title}>{opcao.titulo}</p>
                                    <p className={D.option.description}>{opcao.descricao}</p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className={`${D.alert.container} mt-3 bg-gray-50 border-gray-100`}>
                    <Iconify icon="eva:info-outline" width={16} className="text-gray-400 flex-shrink-0 mt-0.5" />
                    <p className={`${D.alert.text} text-gray-500`}>
                        <Trans
                            t={t}
                            i18nKey="archiveSettings.visibility.permissionHint"
                            components={{ strong: <strong /> }}
                        >
                            Quem vê o quê também depende das permissões de cada usuário
                            (<strong>Arquivamento</strong>, em Configurações → Usuários).
                        </Trans>
                    </p>
                </div>
            </div>

            <div className={D.section.card}>
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                        <div className={D.section.iconBox}>
                            <Iconify icon="eva:folder-outline" width={18} />
                        </div>
                        <div>
                            <p className={D.section.title}>
                                {t('archiveSettings.divisions.sectionTitle', 'Divisões do arquivo')}
                            </p>
                            <p className={D.section.subtitle}>
                                {t(
                                    'archiveSettings.divisions.sectionSubtitle',
                                    'Os estágios em que as conversas arquivadas são organizadas.'
                                )}
                            </p>
                        </div>
                    </div>

                    {podeGerenciar && (
                        <button
                            type="button"
                            className={D.page.saveButton}
                            onClick={() => setModalDivisao({ aberto: true, divisao: null })}
                        >
                            <Iconify icon="eva:plus-outline" width={16} />
                            {t('archiveSettings.divisions.addButton', 'Nova divisão')}
                        </button>
                    )}
                </div>

                {!divisoes.length ? (
                    <div className="flex flex-col items-center justify-center py-10 text-center">
                        <div className="w-14 h-14 rounded-2xl bg-violet-50 flex items-center justify-center text-[#7C3AED] mb-3">
                            <Iconify icon="eva:folder-outline" width={24} />
                        </div>
                        <p className="text-sm font-semibold text-gray-900 mb-1">
                            {t('archiveSettings.divisions.emptyTitle', 'Nenhuma divisão criada')}
                        </p>
                        <p className="text-xs text-gray-500 max-w-sm">
                            {t(
                                'archiveSettings.divisions.emptyDescription',
                                'Sem divisões, os atendentes não conseguem arquivar conversas — é a divisão que define para onde a conversa vai.'
                            )}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {divisoes.map((divisao) => {
                            const cor = getCorDivisao(divisao.cor_hex);
                            return (
                                <div
                                    key={`${divisao._id}`}
                                    className="flex items-center gap-3 p-3.5 rounded-xl border border-gray-200 hover:border-gray-300 transition-all"
                                >
                                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${cor.bg}`}>
                                        <Iconify icon={divisao.icone || 'eva:folder-outline'} width={18} className={cor.text} />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <p className="text-sm font-medium text-gray-900 truncate">{divisao.nome}</p>
                                            <span className="text-xs text-gray-400 tabular-nums">
                                                {t('archiveSettings.divisions.conversationCount', {
                                                    count: divisao.total || 0,
                                                    defaultValue: `${divisao.total || 0} ${
                                                        (divisao.total || 0) === 1 ? 'conversa' : 'conversas'
                                                    }`
                                                })}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                            <span className="text-[11px] text-gray-500">
                                                {divisao.config_desarquivar === 'manual'
                                                    ? t(
                                                          'archiveSettings.divisions.modeManual',
                                                          'Mantém arquivada com nova mensagem'
                                                      )
                                                    : divisao.config_desarquivar === 'auto'
                                                        ? t(
                                                              'archiveSettings.divisions.modeAuto',
                                                              'Desarquiva com nova mensagem'
                                                          )
                                                        : t(
                                                              'archiveSettings.divisions.modeDefault',
                                                              'Usa o padrão da organização'
                                                          )}
                                            </span>
                                            {(divisao.setores || []).length > 0 && (
                                                <>
                                                    <span className="text-[11px] text-gray-300">•</span>
                                                    {divisao.setores.map((setorId) => (
                                                        <span
                                                            key={`${setorId}`}
                                                            className="px-1.5 py-0.5 rounded-md bg-gray-100 text-[10px] font-medium text-gray-600"
                                                        >
                                                            {nomeSetor(setorId)}
                                                        </span>
                                                    ))}
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {podeGerenciar && (
                                        <div className="flex items-center gap-1 flex-shrink-0">
                                            <button
                                                type="button"
                                                className="w-8 h-8 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center transition-all"
                                                onClick={() => setModalDivisao({ aberto: true, divisao })}
                                            >
                                                <Iconify icon="eva:edit-2-outline" width={16} />
                                            </button>
                                            <button
                                                type="button"
                                                className="w-8 h-8 rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-all"
                                                onClick={() => setModalExcluir({ aberto: true, divisao })}
                                            >
                                                <Iconify icon="eva:trash-2-outline" width={16} />
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {!podeGerenciar && (
                    <div className={`${D.alert.container} mt-3`}>
                        <Iconify icon="eva:alert-triangle-outline" width={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <p className={D.alert.text}>
                            <Trans
                                t={t}
                                i18nKey="archiveSettings.divisions.readOnlyWarning"
                                components={{ strong: <strong /> }}
                            >
                                Você não tem a permissão <strong>Gerenciar divisões do arquivo</strong>, então
                                só consegue visualizar. Peça a um administrador em Configurações → Usuários.
                            </Trans>
                        </p>
                    </div>
                )}
            </div>
            </div>

            <ModalDivisao
                aberto={modalDivisao.aberto}
                divisao={modalDivisao.divisao}
                setores={setores}
                onFechar={() => setModalDivisao({ aberto: false, divisao: null })}
                onSalvar={handleSalvarDivisao}
            />

            <ModalExcluirDivisao
                aberto={modalExcluir.aberto}
                divisao={modalExcluir.divisao}
                divisoes={divisoes}
                onFechar={() => setModalExcluir({ aberto: false, divisao: null })}
                onConfirmar={handleExcluirDivisao}
            />
        </div>
    );
}
